import { useCallback, useEffect, useRef, useState, type Dispatch } from 'react';
import { computeDobAuthTag, packQueuePayload, type CreateItemRequest } from '@pink/shared';

import { createQueueItem } from '../../queue-module/queue-client.js';
import { useTenantConfig } from '../../tenant-config-module/use-tenant-config.js';
import { encryptPayload, type PatientPayload } from '../../crypto-module/encrypt-payload.js';
import { generatePatientKeypair } from '../../crypto-module/patient-keypair.js';
import { idbPutKeys, RECOVERY_LIFETIME_MS } from '../../crypto-module/idb.js';
import { clearPatientKeysFromIDB } from '../../crypto-module/clear-patient-keys-from-idb.js';
import { useVerifiedKey } from '../../pinning-module/pinning-context.js';
import { usePatientKeypair } from '../polling-feature/patient-keypair-context.js';
import { TechnicalErrorVariant } from '../technical-error-variant.js';
import type { CancelAction } from '../state-machine-feature/cancel-action.type.js';
import type { AppointmentInfo } from '../../token-module/appointment-info.interface.js';
import { useRawCancelToken } from '../../token-module/use-token-from-route.js';

/**
 * Status-Lifecycle des Submit-Hooks.
 *
 * - `idle`: Hook ist bereit, kann gestartet werden.
 * - `submitting`: Mindestens eine Submit-Sequenz läuft — Doppel-Submits werden
 *   ignoriert (Idempotenz-Guard).
 * - `error`: Letzter Submit ist fehlgeschlagen. Aufrufer kann den Hook
 *   erneut triggern; intern wird der Status zurückgesetzt, sobald `submit`
 *   wieder läuft.
 */
type CancelSubmitStatus = 'idle' | 'submitting' | 'error';

interface UseCancelSubmitOptions {
  readonly appointmentInfo: AppointmentInfo;
  readonly dispatch: Dispatch<CancelAction>;
}

interface UseCancelSubmitResult {
  readonly status: CancelSubmitStatus;
  readonly submit: (dob: string) => void;
}

/**
 * Orchestriert den Submit-Flow nach erfolgreicher DOB-Eingabe gegen die
 * Queue-API (`POST /v1/pink-queue/:tenantId/termin-absage`).
 *
 * Ablauf:
 *  1. Patient-Keypair generieren (RSA-OAEP-3072, Privatschlüssel
 *     non-extractable).
 *  2. `PatientPayload` bauen und mit verifiziertem Tenant-Key hybrid
 *     verschlüsseln → `QueuePayloadFraming`-Triple.
 *  3. Triple via `packQueuePayload` zum Wire-`payload` packen.
 *  4. Frische `absage_id` (UUIDv4) erzeugen und `createQueueItem` aufrufen.
 *  5. `{ tenantId, queueItemId, publicKeyBase64, privateKey, expiresAt }` in
 *     IndexedDB persistieren (für Reload-Recovery).
 *  6. Polling-Context mit dem aktuellen Keypair-Eintrag befüllen, damit
 *     `usePollStatus` ohne IDB-Re-Read direkt starten kann.
 *  7. State-Machine auf `LOADING` schalten.
 *
 * Fehlerfälle (`ApiError`, `CryptoError`, sonstige Exceptions außer
 * `AbortError`) führen zu `GOTO_TECHNICAL_ERROR` Variante `generic` und
 * `clearPatientKeysFromIDB()`. `AbortError` wird still verschluckt, weil
 * der Hook beim Unmount selbst abbricht — kein neuer State-Übergang nötig.
 */
export function useCancelSubmit(options: UseCancelSubmitOptions): UseCancelSubmitResult {
  const tenantKey = useVerifiedKey();
  const tenantConfig = useTenantConfig();
  const rawToken = useRawCancelToken();
  const { setEntry } = usePatientKeypair();
  const [status, setStatus] = useState<CancelSubmitStatus>('idle');
  const stateRef = useRef<HookStateRef>({ status: 'idle', controller: null });
  useEffect(() => () => stateRef.current.controller?.abort(), []);

  const submit = useCallback(
    (dob: string): void => {
      if (rawToken === null) {
        // Sollte nie vorkommen — useCancelSubmit wird unter `/cancel/:token`
        // gemountet. Defensive: still verwerfen, statt einen invaliden
        // Submit zu schicken.
        return;
      }
      startSubmit({
        dob,
        tenantKey,
        tenantId: tenantConfig.tenantId,
        base64Token: rawToken,
        stateRef,
        setStatus,
        setKeypairEntry: setEntry,
        ...options,
      });
    },
    [options, tenantKey, tenantConfig.tenantId, rawToken, setEntry],
  );

  return { status, submit };
}

interface HookStateRef {
  status: CancelSubmitStatus;
  controller: AbortController | null;
}

interface StartSubmitArgs extends UseCancelSubmitOptions {
  readonly dob: string;
  readonly tenantKey: CryptoKey;
  readonly tenantId: string;
  readonly base64Token: string;
  readonly stateRef: { current: HookStateRef };
  readonly setStatus: (next: CancelSubmitStatus) => void;
  readonly setKeypairEntry: (entry: KeypairEntry) => void;
}

interface KeypairEntry {
  readonly tenantId: string;
  readonly queueItemId: string;
  readonly privateKey: CryptoKey;
}

function startSubmit(args: StartSubmitArgs): void {
  if (args.stateRef.current.status === 'submitting') {
    return;
  }
  const updateStatus = (next: CancelSubmitStatus): void => {
    args.stateRef.current.status = next;
    args.setStatus(next);
  };
  updateStatus('submitting');
  const controller = new AbortController();
  args.stateRef.current.controller = controller;
  void runSubmitSequence({
    dob: args.dob,
    appointmentInfo: args.appointmentInfo,
    tenantKey: args.tenantKey,
    tenantId: args.tenantId,
    base64Token: args.base64Token,
    dispatch: args.dispatch,
    signal: controller.signal,
    onSettle: updateStatus,
    setKeypairEntry: args.setKeypairEntry,
  });
}

interface SubmitSequenceArgs {
  readonly dob: string;
  readonly appointmentInfo: AppointmentInfo;
  readonly tenantKey: CryptoKey;
  readonly tenantId: string;
  readonly base64Token: string;
  readonly dispatch: Dispatch<CancelAction>;
  readonly signal: AbortSignal;
  readonly onSettle: (next: CancelSubmitStatus) => void;
  readonly setKeypairEntry: (entry: KeypairEntry) => void;
}

async function runSubmitSequence(args: SubmitSequenceArgs): Promise<void> {
  try {
    await performSubmit(args);
    if (args.signal.aborted) {
      return;
    }
    args.dispatch({ type: 'GOTO_LOADING' });
    args.onSettle('idle');
  } catch (error) {
    if (isAbortError(error)) {
      return;
    }
    await handleSubmitFailure(args.dispatch);
    args.onSettle('error');
  }
}

async function performSubmit(args: SubmitSequenceArgs): Promise<void> {
  const keypair = await generatePatientKeypair();
  const payload = buildPayload(args.dob, args.appointmentInfo, keypair.publicKeyBase64);
  const framing = await encryptPayload(payload, args.tenantKey);
  const authTag = await computeDobAuthTag({
    dob: args.dob,
    tenantId: args.tenantId,
    base64Token: args.base64Token,
  });
  const absageId = crypto.randomUUID();
  const body: CreateItemRequest = {
    absage_id: absageId,
    payload: packQueuePayload(framing),
    base64Token: args.base64Token,
    auth_tag: authTag,
  };
  const item = await createQueueItem(body, {
    tenantId: args.tenantId,
    signal: args.signal,
  });
  if (args.signal.aborted) {
    return;
  }
  await idbPutKeys({
    tenantId: args.tenantId,
    queueItemId: item.id,
    publicKeyBase64: keypair.publicKeyBase64,
    privateKey: keypair.privateKey,
    expiresAt: Date.now() + RECOVERY_LIFETIME_MS,
  });
  args.setKeypairEntry({
    tenantId: args.tenantId,
    queueItemId: item.id,
    privateKey: keypair.privateKey,
  });
}

function buildPayload(
  dob: string,
  appointmentInfo: AppointmentInfo,
  patientPublicKey: string,
): PatientPayload {
  return {
    dob,
    appointmentId: appointmentInfo.appointmentId,
    timeSlot: appointmentInfo.timeSlot,
    location: appointmentInfo.location,
    patientPublicKey,
  };
}

async function handleSubmitFailure(dispatch: Dispatch<CancelAction>): Promise<void> {
  try {
    await clearPatientKeysFromIDB();
  } catch {
    // IDB-Cleanup darf den Error-Pfad nicht verschlucken.
  }
  dispatch({
    type: 'GOTO_TECHNICAL_ERROR',
    variant: TechnicalErrorVariant.Generic,
  });
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
